import * as Sentry from '@sentry/nuxt'
import { Identify, createInstance } from '@amplitude/marketing-analytics-browser'

import type { Customer } from '@brighterly/lib-core-types/customer/interfaces'
import type { AmplitudeOptions } from '@brighterly/lib-core-types/tools/amplitude/interfaces'
import type { Options } from '@amplitude/marketing-analytics-browser/lib/esm/typings/browser-client'

import { useToolsStore } from '@stores/tools'
import { useTutorApplicationStore } from '@stores/tutor-application'

import { isObject, pick } from '@helpers/object'
import { getQueryParamsObject } from '@helpers/query'

import { getUserAgentInfo } from './user-agent'

export const amplitude = createInstance()

export const initAmplitude = (options: AmplitudeOptions = {}): void => {
  const toolsStore = useToolsStore()

  const runtimeConfig = useRuntimeConfig().public
  const apiKey = runtimeConfig.amplitudeApiKey ?? ''
  const env = runtimeConfig.environment
  const serverUrl = runtimeConfig.amplitudeServerUrl

  if (!apiKey) {
    console.error('Error in method initAmplitude: amplitudeApiKey is not defined.')
  } else {
    if (!toolsStore._analyticsFirstSuccessInit) {
      setAmplitudeUserProperty('environment', env || '')
      setAmplitudeUserProperty('user_type', 'candidate')

      const queryParams = getQueryParamsObject() as Record<string, string>
      if (isObject(queryParams)) {
        Object.entries(queryParams).forEach(([key, value]) => {
          if (value) {
            setAmplitudeUserProperty(key, value)
          }
        })
      }
    }

    const baseOptions: Options = {
      pageViewTracking: true,
      deviceId: options.deviceId || undefined,
    }

    if (serverUrl) {
      baseOptions.serverUrl = serverUrl
    }

    amplitude.init(
      apiKey,
      options.userId || undefined,
      baseOptions,
    )
      .promise
      .then((): void => {
        toolsStore._setAnalyticsFirstSuccessInit(true)
        const amplitudeId = options.deviceId || amplitude.getDeviceId()
        const amplitudeUrl = `https://app.amplitude.com/analytics/skelar/project/472284/search/${amplitudeId}`
        const tutorApplicationStore = useTutorApplicationStore()
        tutorApplicationStore.setAmplitudeId(amplitudeId || '')
        if (!toolsStore._analyticsFirstSuccessInit) {
          const dsn = useRuntimeConfig().public.sentryDsn
          if (dsn) {
            Sentry.setUser({
              amplitude_url: amplitudeUrl,
            })
          }
        }
      })
      .catch((): void => {
        const timeout = setTimeout(() => {
          initAmplitude(options)
          clearTimeout(timeout)
        }, 800)
      })
  }
}

export const trackEvent = (eventName: string, eventProps?: Record<string, any>, callback = (): void => {}): void => {

  const props = pick({
    app: 'hiring-app',
    ...(eventProps || {}),
  })

  const hasProps = Object.keys(props).length > 0

  const deviceProps = getUserAgentInfo()
  const isCallback = (typeof callback === 'function')
  if (!hasProps) {
    amplitude
      .track(eventName, { ...deviceProps }).promise
      .finally(() => {
        if (isCallback) {
          callback()
        }
      })
  } else {
    amplitude
      .track(eventName, { ...deviceProps, ...props }).promise
      .finally(() => {
        if (isCallback) {
          callback()
        }
      })
  }
}

export const setAmplitudeUserProperty = (name: string, value: string, once: boolean = false): void => {
  const identify = new Identify()
  identify[once ? 'setOnce' : 'set'](name, value)
  amplitude.identify(identify)
}

export const setUserPropertiesFromQuery = (callback = (): void => {
}): void => {
  const paramsObj = getQueryParamsObject() as string[]
  const paramsKeys = Object.keys(paramsObj)
  if (paramsKeys.length > 0) {
    const identifySetOnceObj = new Identify()

    paramsKeys.forEach(param => {
      const query = paramsObj[param as keyof typeof paramsObj] as any
      identifySetOnceObj.setOnce(`initial_${param}`, query)
      identifySetOnceObj.set(param, query)
    })
    amplitude
      .identify(identifySetOnceObj)
      .promise.finally(() => {
        callback?.()
      })
  }
}

export const setMarketingUserProperties = (data: Customer['marketing_data']): void => {
  if (!isObject(data)) {
    console.warn('Invalid data for booking user properties.')
  } else {
    const identifySetOnceObj = new Identify()
    Object.keys(data).map(key => {
      // @ts-expect-error any type data key
      const value = data[key]
      if (value !== null) {
        identifySetOnceObj.set(key, value)
      }
      return key
    })
    amplitude.identify(identifySetOnceObj)
  }
}

export const setBookingUserProperties = (data: Customer['marketing_data']): void => {
  if (!isObject(data)) {
    console.warn('Invalid data for booking user properties.')
  } else {
    const identifySetOnceObj = new Identify()
    Object.keys(data).map(key => {
      // @ts-expect-error any type data key
      const value = data[key]
      if (value !== null) {
        identifySetOnceObj.setOnce(`booking_${key}`, value)
      }
      return key
    })
    amplitude.identify(identifySetOnceObj)
  }
}
